export const OVERVIEW_PATH = '/voice-agents';
export const VOICE_AGENT_PATH = '/assistants/phone/:channelId';
export const VOICE_AGENT_RECORD_PATH = '/assistants/phone/:channelId/records/:recordId';
export const VOICE_AGENT_CONFIG_PATH = '/assistants/phone/:channelId/config/:tab?';
export const LEGACY_VOICE_AGENT_PATH = '/voice-agents/:channelId';
export const LEGACY_VOICE_AGENT_RECORD_PATH = '/voice-agents/:channelId/records/:recordId';
export const LEGACY_VOICE_AGENT_CONFIG_PATH = '/voice-agents/:channelId/config/:tab?';

// ElevenLabs knowledge base accepts common document/text formats.
export const KNOWLEDGE_FILE_ACCEPT: Record<string, string[]> = {
    'text/plain': ['.txt'],
    'text/html': ['.html'],
    'text/markdown': ['.md'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

export const KNOWLEDGE_SUPPORTED_EXTENSIONS = new Set(Object.values(KNOWLEDGE_FILE_ACCEPT).flat());
export const KNOWLEDGE_SUPPORTED_EXTENSIONS_LABEL = [...KNOWLEDGE_SUPPORTED_EXTENSIONS].sort().join(', ');

export const LANGUAGE_OPTIONS = [
    { value: 'de', label: 'German' },
    { value: 'en', label: 'English' },
];

// What the caller states as second factor, read from the contact the caller identification lookup matched: its
// customer number or an attribute of its extra data. The lowercase value is the canonical token the backend stores.
// Extra data has no standard question, so it needs one configured.
export const CALLER_VERIFICATION_FIELD_CUSTOMER_NUMBER = 'customer_number';
export const CALLER_VERIFICATION_FIELD_EXTRA_DATA = 'extra_data';
export const CALLER_VERIFICATION_FIELDS = [
    CALLER_VERIFICATION_FIELD_CUSTOMER_NUMBER,
    CALLER_VERIFICATION_FIELD_EXTRA_DATA,
];
export const CALLER_VERIFICATION_FIELDS_WITHOUT_DEFAULT_LABEL = new Set([CALLER_VERIFICATION_FIELD_EXTRA_DATA]);
// What the agent asks for the customer number when no question is configured — mirrors the backend's
// CALLER_VERIFICATION_DEFAULT_QUESTIONS.
export const CALLER_VERIFICATION_DEFAULT_QUESTIONS: Record<string, string> = {
    de: 'Bitte nennen Sie mir Ihre Kundennummer.',
    en: 'Please tell me your customer number.',
};
export const CALLER_VERIFICATION_ATTEMPTS_MIN = 1;
export const CALLER_VERIFICATION_ATTEMPTS_MAX = 5;
export const CALLER_VERIFICATION_ATTEMPTS_DEFAULT = 3;

// Which ElevenLabs TTS model speaks. The lowercase value is the canonical token the backend stores.
export const VOICE_MODEL_CONVERSATIONAL = 'conversational_voice';
export const VOICE_MODEL_FAST = 'fast_voice';

// TTS speaking rate — the range ElevenLabs accepts for the voice speed setting (fast voice only).
export const VOICE_SPEED_MIN = 0.7;
export const VOICE_SPEED_MAX = 1.2;
export const VOICE_SPEED_DEFAULT = 1.0;
export const VOICE_SPEED_STEP = 0.05;
// Maximum length of a single call in seconds.
export const MAX_CALL_DURATION_MIN = 60;
export const MAX_CALL_DURATION_MAX = 3600;
export const MAX_CALL_DURATION_DEFAULT = 1200;
export const MAX_CALL_DURATION_STEP = 60;

// Tools the user can switch on/off in the wizard. The lowercase value is the canonical token used everywhere
// (GraphQL enabledTools, the ElevenLabs tool name, and the references in the prompt).
export const CONFIGURABLE_TOOLS = [
    'article_search',
    'order_history',
    'order_lookup',
    'open_offers',
    'delivery_address',
    'client_search',
    'internal_contact_search',
    'forward_to_internal_number',
    'get_teams_status',
    'send_teams_message',
    'send_email',
    'meeting_availability',
    'book_meeting',
    'create_order',
    'create_rfq',
] as const;

// Tools that only work with a connected Microsoft Teams integration. forward_to_internal_number is NOT in here:
// it only uses the Teams status gate when get_teams_status is enabled, otherwise it forwards directly.
export const TEAMS_DEPENDENT_TOOLS = ['get_teams_status', 'send_teams_message'];

// Tools that answer out of the ERP the agent is connected to, and are therefore unusable until one is picked.
export const ERP_DEPENDENT_TOOLS = ['article_price', 'article_availability'];

// Tools that expose customer master data to anyone who calls — the caller's phone number is not proof of who they
// are. Marked as dangerous in the config UI and only switched on after the user confirmed the risk.
export const DANGEROUS_TOOLS = ['client_search'];

export const DEFAULT_ENABLED_TOOLS = [
    'article_search',
    'order_history',
    'open_offers',
    'delivery_address',
    'create_order',
];

// Editable prompt (ENG-11790): the GraphQL enum names of the backend enums VoiceAgentPromptGuardrail,
// VoiceAgentPromptFormality and VoiceAgentPromptApproach. Labels and descriptions live in the locale under
// form.prompt; the sentence a guardrail injects into the prompt is backend-internal.
export const PROMPT_GUARDRAILS = [
    'NO_COMPANY_POLICY_TOPICS',
    'NO_PRICING_TALK',
    'NEVER_PROMISE_FOLLOW_UPS',
    'NO_PRODUCT_ADVICE',
    'NO_ALTERNATIVE_ARTICLES',
    'NO_FORWARDING',
    'NO_PERSONAL_DATA_BEYOND_NEED',
    'NO_MEDICAL_DATA',
    'ONE_DOCUMENT_PER_CALL',
    'NO_COMPATIBILITY_CLAIMS',
    'NEVER_INVENT_PRODUCT_ATTRIBUTES',
    'NO_DISCOUNT_OR_AVAILABILITY_PROMISES',
] as const;
export type PromptGuardrail = (typeof PROMPT_GUARDRAILS)[number];

export const PROMPT_FORMALITY_OPTIONS = ['FORMAL', 'INFORMAL'] as const;
export type PromptFormality = (typeof PROMPT_FORMALITY_OPTIONS)[number];
export const PROMPT_DEFAULT_FORMALITY: PromptFormality = 'FORMAL';

export const PROMPT_APPROACH_OPTIONS = ['GUIDING', 'STRAIGHT_TO_THE_POINT'] as const;
export type PromptApproach = (typeof PROMPT_APPROACH_OPTIONS)[number];
export const PROMPT_DEFAULT_APPROACH: PromptApproach = 'GUIDING';

// Flow templates come from the backend (voiceAgentEditablePromptDefaults); these keys order the menus. A full
// flow replaces the phase list, an add-on appends one phase. "order" is also what a new agent starts with.
export const PROMPT_FLOW_TEMPLATES = ['order', 'order_dictation', 'rfq'] as const;
export const PROMPT_ADD_ON_TEMPLATES = ['callback_note', 'availability_inquiry', 'order_status'] as const;
export const PROMPT_DEFAULT_FLOW_TEMPLATE = 'order';
